import { applyLanguage, LanguageProvider, text, t, ensureMessages } from './i18n';
import { createRoot } from 'react-dom/client';
import {
  Component,
  Suspense,
  lazy,
  useEffect,
  useLayoutEffect,
  type ComponentType,
  type ReactNode,
} from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient, schedulePortalWarmup } from './queries';
import { AuthProvider, useAuth } from './auth';
import { ToastProvider } from './toast';
import { redirectToLogin } from './api';
import { AppLayout } from './layout';
import { applyThemeMode, storedThemeMode } from './theme';
import { AvailableModelsPageSkeleton } from './features/models/ModelsPageSkeleton';
import { RequestHistoryPageSkeleton } from './features/requests/RequestHistoryPageSkeleton';
import { UsagePageSkeleton } from './features/usage/UsagePageSkeleton';
import { useRoute, type Route, type RouteId } from './router';

// Each page loads as its own chunk so the shell (layout + skeletons) stays
// small; the matching skeleton doubles as the Suspense fallback.
function lazyPage<K extends string>(loader: () => Promise<Record<K, ComponentType>>, name: K) {
  return lazy(() => loader().then((module) => ({ default: module[name] })));
}

class PageLoadBoundary extends Component<
  { children: ReactNode; resetKey: string },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidUpdate(previous: Readonly<{ children: ReactNode; resetKey: string }>) {
    if (previous.resetKey !== this.props.resetKey && this.state.failed) {
      this.setState({ failed: false });
    }
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <section className="card page-error-state" role="alert">
        <strong>{t('common.this_page_couldn_t_load')}</strong>
        <span>
          {text(
            '请刷新页面重试。如果问题仍然存在，请稍后再试。',
            'Reload the page to try again. If the problem continues, try again later.',
          )}
        </span>
        <div>
          <button className="btn-primary" type="button" onClick={() => window.location.reload()}>
            {t('common.reload_page')}
          </button>
        </div>
      </section>
    );
  }
}

const HomePage = lazyPage(() => import('./features/dashboard/HomePage'), 'HomePage');
const EndpointsPage = lazyPage(() => import('./features/endpoints/EndpointsPage'), 'EndpointsPage');
const EndpointDetailPage = lazyPage(
  () => import('./features/endpoints/EndpointDetailPage'),
  'EndpointDetailPage',
);
const ModelsPage = lazyPage(() => import('./features/models/ModelsPage'), 'ModelsPage');
const KeysPage = lazyPage(() => import('./features/keys/KeysPage'), 'KeysPage');
const RequestHistoryPage = lazyPage(
  () => import('./features/requests/RequestHistoryPage'),
  'RequestHistoryPage',
);
const TeamPage = lazyPage(() => import('./features/orgs/TeamPage'), 'TeamPage');
const UsagePage = lazyPage(() => import('./features/usage/UsagePage'), 'UsagePage');
const LoginPage = lazyPage(() => import('./features/auth/LoginPage'), 'LoginPage');
const SecurityPage = lazyPage(() => import('./features/security/SecurityPage'), 'SecurityPage');
const SupportPage = lazyPage(() => import('./features/support/SupportPage'), 'SupportPage');

const root = document.getElementById('portal-root');

if (!root) {
  throw new Error('Missing #portal-root');
}
const portalRoot = root;

applyLanguage();

function App() {
  return (
    <LanguageProvider>
      <AppShell />
    </LanguageProvider>
  );
}

function AppShell() {
  const route = useRoute();

  useEffect(() => {
    document.title = `${route.title} - Antix Console`;
  }, [route.title]);

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const syncSystemTheme = () => {
      const mode = storedThemeMode();
      if (mode === 'system') applyThemeMode(mode);
    };

    syncSystemTheme();
    media.addEventListener('change', syncSystemTheme);
    return () => media.removeEventListener('change', syncSystemTheme);
  }, []);

  useLayoutEffect(() => {
    if (route.id !== 'login') return;
    resetPortalShell();
  }, [route.id]);

  if (route.id === 'login') {
    return (
      <PageLoadBoundary resetKey={route.id}>
        <Suspense fallback={null}>
          <LoginPage />
        </Suspense>
      </PageLoadBoundary>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <AuthProvider>
          <AuthenticatedPortal route={route} />
        </AuthProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
}

function resetPortalShell() {
  document.body.className = '';
  delete document.body.dataset.page;
}

function PageLoadingSkeleton({ routeId }: { routeId: RouteId }) {
  if (routeId === 'usage') {
    return <UsagePageSkeleton />;
  }
  if (routeId === 'history') {
    return <RequestHistoryPageSkeleton />;
  }
  if (routeId === 'models') {
    return <AvailableModelsPageSkeleton />;
  }
  return (
    <div className="portal-route-loading" aria-busy="true" aria-label={t('common.loading')}>
      <div className="page-loading-bar" aria-hidden="true" />
    </div>
  );
}

function AuthenticatedPortal({ route }: { route: Route }) {
  const { user, loading, authError } = useAuth();

  useLayoutEffect(() => {
    if (loading || !user) resetPortalShell();
  }, [loading, user]);

  useEffect(() => {
    if (loading || user) return;
    redirectToLogin(authError || undefined);
  }, [authError, loading, user]);

  if (loading) {
    return <PortalAuthLoading />;
  }

  if (!user) {
    return null;
  }

  return (
    <AppLayout route={route}>
      <PortalRoute route={route} />
    </AppLayout>
  );
}

function PortalAuthLoading() {
  return (
    <div className="portal-auth-loading" aria-busy="true" aria-label={t('common.loading_account')}>
      <img src="/img/favicon.svg" alt="" />
    </div>
  );
}

function PortalRoute({ route }: { route: Route }) {
  const { accessLevel, loading } = useAuth();

  useEffect(() => {
    // The history page schedules the same warmup after its own request succeeds.
    if (loading || route.id === 'history') return;
    return schedulePortalWarmup();
  }, [loading, route.id]);

  const isPublicRoute = route.id === 'home' || route.id === 'support';
  const required = isPublicRoute ? 'public' : 'standard';
  const rank = { public: 0, standard: 1, admin: 2 } as const;

  if (loading && !isPublicRoute) {
    return <PageLoadingSkeleton routeId={route.id} />;
  }

  if (rank[accessLevel] < rank[required]) {
    return (
      <div className="card page-error-state">
        {text(
          '您当前的账号级别无权访问此页面。',
          'Your account tier does not have access to this page.',
        )}
      </div>
    );
  }

  return (
    <PageLoadBoundary resetKey={route.id}>
      <Suspense fallback={<PageLoadingSkeleton routeId={route.id} />}>
        {renderRoute(route)}
      </Suspense>
    </PageLoadBoundary>
  );
}

function renderRoute(route: Route) {
  switch (route.id) {
    case 'team':
      return <TeamPage />;
    case 'security':
      return <SecurityPage />;
    case 'keys':
      return <KeysPage />;
    case 'models':
      return <ModelsPage />;
    case 'usage':
      return <UsagePage />;
    case 'history':
      return <RequestHistoryPage />;
    case 'support':
      return <SupportPage />;
    case 'endpoints':
      return <EndpointsPage />;
    case 'endpointDetail':
      return <EndpointDetailPage />;
    case 'home':
    default:
      return <HomePage />;
  }
}

async function bootstrap() {
  await ensureMessages();
  createRoot(portalRoot).render(<App />);
}

void bootstrap();
